import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import clsx from 'clsx';

import { useOutsideClick } from '@/hooks/ui/useOutsideClick';
import {
  fetchTaskTemplates,
  TaskTemplate,
} from '@/entities/task/api/templates';
import { textOf } from '@/shared/utils/nullable';
import { FloatingPanel } from '@/shared/ui/FloatingPanel';
import { normalizeIds, onAltArrowDownOpen } from '../utils';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ANY = any;

/** Дата шаблона «дд.мм.гггг». `b_tasks_template` хранит эти колонки не во всех
 *  версиях коробки — если их нет, поле приезжает пустым и строка молчит. */
function templateDate(iso?: string): string {
  if (!iso) {
    return '';
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime()) || d.getFullYear() < 1971) {
    return '';
  }
  return d.toLocaleDateString('ru-RU');
}

interface DTemplateDropdownProps {
  disabled?: boolean;
  onApplyTemplate: (t: TaskTemplate) => void;
  variant?: 'pill' | 'field' | 'proto' | 'inline';
}

export function DTemplateDropdown({
  disabled,
  onApplyTemplate,
  variant = 'pill',
}: DTemplateDropdownProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [templates, setTemplates] = useState<TaskTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(
    null
  );
  const [q, setQ] = useState('');

  const wrapRef = useRef<HTMLDivElement | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  useOutsideClick({
    enabled: open,
    refs: [btnRef as ANY, panelRef as ANY],
    onOutside: () => setOpen(false),
  });

  useEffect(() => {
    if (!open || disabled || templates.length > 0) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const list = await fetchTaskTemplates();
        if (!cancelled) {
          setTemplates(list);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setError('Не удалось загрузить шаблоны');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, disabled, templates.length]);

  useEffect(() => {
    if (!open) {
      setQ('');
      return;
    }
    const t = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  const selected = useMemo(() => {
    if (!selectedTemplateId) {
      return null;
    }
    return templates.find((t) => t.ID === selectedTemplateId) ?? null;
  }, [templates, selectedTemplateId]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) {
      return templates;
    }
    return templates.filter((t) => {
      const title = textOf(t.TITLE).toLowerCase();
      const desc = textOf(t.DESCRIPTION).toLowerCase();
      return title.includes(s) || desc.includes(s) || String(t.ID).includes(s);
    });
  }, [templates, q]);

  const isField = variant === 'field';
  const isProto = variant === 'proto';
  const isInline = variant === 'inline';

  return (
    <div ref={wrapRef} className={isField ? 'relative w-full' : 'relative'}>
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onAltArrowDownOpen(open, () => setOpen(true))}
        className={
          isInline
            ? clsx(
                'inline-flex items-center gap-[5px] border-b border-[rgba(59,77,127,0.3)] pb-[2px]',
                'text-[12px] font-bold text-[#141E31] transition-colors',
                disabled
                  ? 'cursor-not-allowed opacity-50'
                  : 'hover:border-[#57B792]'
              )
            : isProto
              ? clsx(
                  'inline-flex items-center gap-[8px] whitespace-nowrap rounded-full border border-[rgba(59,77,127,0.16)] bg-white px-[14px] py-[9px]',
                  'text-[12px] font-semibold text-[#141E31] transition-colors',
                  disabled
                    ? 'cursor-not-allowed opacity-50'
                    : 'hover:border-[#3B4D7F]'
                )
              : isField
                ? clsx(
                    'flex h-[44px] w-full items-center justify-between gap-[10px] rounded-[16.98px] bg-[#F8F7FC] px-[11px]',
                    'shadow-[0_3.54px_12.88px_rgba(0,0,0,0.03)] border border-white/40',
                    'text-[16px] leading-[130%] text-[#000000]/70',
                    disabled && 'opacity-50 cursor-not-allowed'
                  )
                : clsx(
                    'inline-flex h-[44px] items-center gap-[10px] rounded-[24px] bg-white px-[18px]',
                    'shadow-[0_3.54px_12.88px_rgba(0,0,0,0.06)] border border-black/5',
                    'text-[16px] leading-[130%] tracking-[-0.83px] text-[#000000]',
                    disabled && 'opacity-50 cursor-not-allowed'
                  )
        }
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <span
          className={clsx(
            'truncate',
            isInline
              ? 'max-w-[140px]'
              : isProto
                ? 'max-w-[200px]'
                : 'max-w-[280px]'
          )}
        >
          {selected
            ? textOf(selected.TITLE)
            : isInline
              ? 'шаблон'
              : 'Выбрать шаблон'}
        </span>
        <ChevronDown
          className={clsx(
            'shrink-0 transition',
            isInline
              ? 'w-[11px] h-[11px] text-[rgba(20,30,49,0.45)]'
              : isProto
                ? 'w-[12px] h-[12px] text-[rgba(20,30,49,0.45)]'
                : 'w-[14px] h-[14px]',
            open && 'rotate-180'
          )}
        />
      </button>

      {open ? (
        <FloatingPanel
          ref={panelRef}
          anchorRef={wrapRef}
          align={isField ? 'stretch' : 'end'}
          sideOffset={8}
          className={clsx(
            'rounded-[12px] border border-black/10 bg-white p-2 shadow-[0_12px_30px_rgba(0,0,0,0.18)]',
            !isField &&
              (isProto || isInline
                ? 'w-[300px] max-w-[calc(100vw-48px)]'
                : 'w-[360px] max-w-[calc(100vw-48px)]')
          )}
          role="menu"
        >
          <div className="flex items-center gap-2 rounded-[10px] border border-black/10 bg-[#F8F7FC] px-2 py-2">
            <Search className="h-4 w-4 shrink-0 text-black/40" />
            <input
              ref={searchRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Поиск по шаблонам..."
              disabled={disabled}
              className="w-full bg-transparent text-[13px] text-[#14222C] outline-none placeholder:text-black/40"
            />
            {q.trim() ? (
              <button
                type="button"
                onClick={() => {
                  setQ('');
                  requestAnimationFrame(() => searchRef.current?.focus());
                }}
                className="flex h-6 w-6 items-center justify-center rounded-lg transition hover:bg-black/5"
                aria-label="Очистить поиск"
              >
                <X className="h-4 w-4 text-black/50" />
              </button>
            ) : null}
          </div>

          {error ? (
            <div className="px-2 py-1 text-[12px] text-red-500">{error}</div>
          ) : null}

          <div className="mt-2 max-h-[280px] overflow-auto pr-1">
            {loading ? (
              <div className="px-2 py-3 text-[13px] text-black/50">
                Загрузка...
              </div>
            ) : templates.length === 0 ? (
              <div className="px-2 py-3 text-[13px] text-black/50">
                Шаблонов нет
              </div>
            ) : filtered.length === 0 ? (
              <div className="px-2 py-3 text-[13px] text-black/50">
                Ничего не найдено
              </div>
            ) : (
              <div className="space-y-1">
                {filtered.map((t) => {
                  const active = selectedTemplateId === t.ID;
                  return (
                    <button
                      key={t.ID}
                      type="button"
                      className={clsx(
                        'w-full rounded-[10px] px-2 py-2 text-left transition',
                        active ? 'bg-[#57B7921A]' : 'hover:bg-black/5'
                      )}
                      onClick={() => {
                        setSelectedTemplateId(t.ID);
                        onApplyTemplate(t);
                        setOpen(false);
                      }}
                      role="menuitem"
                    >
                      <div className="truncate text-[13px] font-medium text-[#14222C]">
                        {textOf(t.TITLE) || `Шаблон #${t.ID}`}
                      </div>
                      <div className="line-clamp-2 text-[12px] text-black/55">
                        {textOf(t.DESCRIPTION) || 'Без описания'}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-black/45">
                        <span>Приоритет: {String(t.PRIORITY ?? '0')}</span>
                        <span>
                          Помощники: {normalizeIds(t.ACCOMPLICES).length}
                        </span>
                        <span>
                          Наблюдатели: {normalizeIds(t.AUDITORS).length}
                        </span>
                        {templateDate(t.CHANGED_DATE || t.CREATED_DATE) && (
                          <span>
                            {t.CHANGED_DATE ? 'Изменён' : 'Создан'}:{' '}
                            {templateDate(t.CHANGED_DATE || t.CREATED_DATE)}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </FloatingPanel>
      ) : null}
    </div>
  );
}
